import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Clock, Coffee, Inbox, MapPin, Navigation, RefreshCw, Search, Users, X } from 'lucide-react';
import './CrewWidgets.css';
import FloatMenuButton, { FloatMenuOption } from '../../components/v2/FloatMenuButton';
import { CrewMemberStatus, CrewSupervisor } from '../../interfaces/CrewLiveStatus';
import {
  avatarColor, clockedOutText, costCodeText, CREW_STAT, CrewDisplayStatusKey,
  displayStatus, fullName, initials, locLabel, locMode, wageTypeLabel, workSummary,
} from './crewUtil';
import CrewPunchTable from './CrewPunchTable';

interface CrewLiveStatusCardProps {
  title: string;
  /** Full crew (summary counts always reflect the whole crew). */
  crew: CrewMemberStatus[];
  loading: boolean;
  error: boolean;
  /** Super admins get the supervisor picker; others the plain "{n} employees" count. */
  showSupervisorPicker: boolean;
  supervisors: CrewSupervisor[];
  selectedSupervisorID: number | null;
  onSupervisorChange: (employeeID: number) => void;
  onRefresh: () => void;
  selectedEmployeeID: number | null;
  onRowClick: (member: CrewMemberStatus) => void;
  /** Re-render tick for elapsed text (30s granularity). */
  nowMs: number;
}

export const StatusPill: React.FC<{ status: CrewDisplayStatusKey }> = ({ status }) => {
  const meta = CREW_STAT[status];
  return (
    <span className={`pill ${meta.pill}`}>
      {status === 'in' && <Clock />}
      {status === 'meal' && <Coffee />}
      {meta.label}
    </span>
  );
};

const CrewLiveStatusCard: React.FC<CrewLiveStatusCardProps> = ({
  title, crew, loading, error,
  showSupervisorPicker, supervisors, selectedSupervisorID, onSupervisorChange,
  onRefresh, selectedEmployeeID, onRowClick, nowMs,
}) => {
  const onClockN = crew.filter((c) => c.status === 'in').length;
  const mealN = crew.filter((c) => c.status === 'meal').length;
  // "Clocked out" = worked today and clocked out; "No work" = no punches today.
  const outN = crew.filter((c) => displayStatus(c) === 'out').length;
  const noneN = crew.filter((c) => displayStatus(c) === 'none').length;

  // Client-side name filter, local to this card. It used to be piped in from Home's
  // page-head search box; that box moved to the global header, which searches every
  // employee rather than narrowing this list, so the card owns its own filter now.
  const [filterTerm, setFilterTerm] = useState('');

  const term = filterTerm.trim().toLowerCase();
  const rows = term
    ? crew.filter((c) => fullName(c).toLowerCase().includes(term))
    : crew;

  // Multi-open caret expansion — matches prototype's classList.toggle('open') per item.
  // Track by employeeID so stable across re-renders. Collapse all when the crew list
  // identity changes (different supervisor selected or first load).
  const [expandedIDs, setExpandedIDs] = useState<Set<number>>(new Set());

  // Crew identity signature: sorted employee IDs joined. Changes when the crew
  // array gets replaced (supervisor switch or initial load).
  const crewSig = crew.map((c) => c.employeeID).sort((a, b) => a - b).join(',');
  const prevCrewSig = useRef<string>('');
  useEffect(() => {
    if (crewSig !== prevCrewSig.current) {
      prevCrewSig.current = crewSig;
      setExpandedIDs(new Set());
      // Clear the filter too — a term typed against the previous crew would otherwise
      // hide the new one behind a "No matches" the user didn't ask for.
      setFilterTerm('');
    }
  }, [crewSig]);

  // Crew size rides as the option count (right-aligned in the menu, like Project Map's
  // filters) rather than being glued onto the name.
  const supMenuOptions: FloatMenuOption[] = useMemo(
    () => supervisors.map((s) => ({
      value: String(s.employeeID),
      label: `${s.name.first} ${s.name.last}`,
      count: s.crewCount,
    })),
    [supervisors]
  );

  const selectedSupLabel = useMemo(() => {
    const sel = supervisors.find((s) => s.employeeID === selectedSupervisorID);
    return sel ? `${sel.name.first} ${sel.name.last}` : 'Select…';
  }, [supervisors, selectedSupervisorID]);

  const toggleExpanded = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setExpandedIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="card crew-card">
      <div className="card-head">
        <Users />
        <h3>{title}</h3>
        <span className="right">
          <div className="crew-filter">
            <Search />
            <input
              type="text"
              value={filterTerm}
              placeholder="Filter crew…"
              aria-label="Filter crew by name"
              onChange={(e) => setFilterTerm(e.target.value)}
            />
            {filterTerm !== '' && (
              <button type="button" className="cf-x" onClick={() => setFilterTerm('')} aria-label="Clear filter">
                <X />
              </button>
            )}
          </div>
          {showSupervisorPicker ? (
            // Always has a supervisor selected — there is no "all crews" baseline to
            // contrast against, so the trigger stays neutral rather than filter-active.
            <FloatMenuButton
              label="Supervisor"
              valueLabel={selectedSupLabel}
              isPlaceholder={selectedSupervisorID == null}
              isActive={false}
              options={supMenuOptions}
              selectedValue={selectedSupervisorID == null ? '' : String(selectedSupervisorID)}
              onSelect={(v) => onSupervisorChange(Number(v))}
              disabled={supervisors.length === 0}
              searchable
            />
          ) : (
            <span className="crew-head-count">{crew.length} employees</span>
          )}
          <button className="btn ghost sm" type="button" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={loading ? 'crew-spin' : undefined} />
            Refresh
          </button>
        </span>
      </div>

      <div className="crew-summary">
        <span className="cs-chip"><span className="cs-dot dot-in" /><b>{onClockN}</b> on the clock</span>
        <span className="cs-chip"><span className="cs-dot dot-meal" /><b>{mealN}</b> at meal</span>
        <span className="cs-chip"><span className="cs-dot dot-out" /><b>{outN}</b> clocked out</span>
        <span className="cs-chip"><span className="cs-dot dot-none" /><b>{noneN}</b> no work</span>
      </div>

      <div className="crew-list">
        {loading && crew.length === 0 ? (
          <div className="crew-empty"><span className="crew-loader" aria-label="Loading" role="status" /></div>
        ) : error && crew.length === 0 ? (
          <div className="crew-empty">
            <Inbox />
            <div className="et">Couldn't load crew status</div>
            <p>Try Refresh in a moment.</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="crew-empty">
            <Inbox />
            <div className="et">{term ? 'No matches' : 'No crew members'}</div>
            <p>
              {term
                ? `No crew members match "${filterTerm.trim()}".`
                : 'No employees found in this crew.'}
            </p>
          </div>
        ) : (
          rows.map((c) => {
            const name = fullName(c);
            const off = c.status === 'out';
            const wage = wageTypeLabel(c.wageType);
            // Show caret + table for ANY row that has punches (incl. clocked-out)
            const hasPunches = c.punches.length > 0;
            const isExpanded = hasPunches && expandedIDs.has(c.employeeID);
            // "since {first punch-in today} · {running worked total, meals excluded}"
            const ws = workSummary(c, nowMs);

            return (
              <div
                key={c.employeeID}
                className={`crew-item${isExpanded ? ' open' : ''}`}
              >
                {/* Fix 6: scope .off opacity to the row only (not the expanded punch panel) */}
                <div
                  className={`crew-row${off ? ' off' : ''}${selectedEmployeeID === c.employeeID ? ' active' : ''}`}
                  onClick={() => onRowClick(c)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(c); } }}
                >
                  <span className="crew-av" style={{ background: avatarColor(name) }}>
                    {initials(name)}
                    {!off && <span className={`liveping ${CREW_STAT[c.status].dot}`} />}
                  </span>
                  <div className="crew-id">
                    <div className="crew-name">{name}</div>
                    <div className="crew-role">
                      {c.jobTitle || '—'}
                      {!off && wage ? ` · ${wage}` : ''}
                    </div>
                  </div>
                  {off ? (
                    <div className="crew-job"><div className="cj-off">{clockedOutText(c, nowMs)}</div></div>
                  ) : locMode(c) !== null ? (() => {
                    const mode = locMode(c);
                    const csw = c.customerServiceWarranty;
                    return (
                      // Location-based: no project, on-clock. Show navigation icon + label.
                      <div className="crew-job">
                        <div className="cj-name"><Navigation className="ic" />{locLabel(c)}</div>
                        {/* Sub-lines vary by mode */}
                        {mode === 'csw' && csw ? (
                          <>
                            <div className="cj-code">
                              {csw.name}{csw.description ? ` · ${csw.description}` : ''}
                            </div>
                            {csw.warrantyProject && (
                              <div className="cj-code">
                                {csw.warrantyProject.name} · {csw.warrantyProject.altProjectID}
                              </div>
                            )}
                          </>
                        ) : mode === 'service' && c.serviceOrder?.name ? (
                          <div className="cj-code">{c.serviceOrder.name}</div>
                        ) : mode === 'hourly' && c.accountCode ? (
                          <div className="cj-code">{c.accountCode.altAccountCodeID} · {c.accountCode.name}</div>
                        ) : null}
                      </div>
                    );
                  })() : (
                    <div className="crew-job">
                      <div className="cj-name"><MapPin className="ic" />{c.project?.name || '—'}</div>
                      <div className="cj-code">{costCodeText(c)}</div>
                    </div>
                  )}
                  <div className="crew-status">
                    <StatusPill status={displayStatus(c)} />
                    {ws && (
                      <div className="cst-time">
                        since {ws.since} · <b>{ws.worked}</b>
                      </div>
                    )}
                  </div>
                  {hasPunches ? (
                    <button
                      className="crew-exp"
                      type="button"
                      title="View today's punches"
                      onClick={(e) => toggleExpanded(e, c.employeeID)}
                      aria-expanded={isExpanded}
                    >
                      <span className="ce-tx">Punches</span>
                      <ChevronDown className="ic" />
                    </button>
                  ) : (
                    <span className="crew-exp-sp" />
                  )}
                </div>
                {hasPunches && (
                  <div className="crew-punches">
                    <CrewPunchTable
                      punches={c.punches}
                      status={c.status}
                      nowMs={nowMs}
                      kioskUser={c.kioskUser}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CrewLiveStatusCard;
